import { useCallback, useEffect, useState, useRef } from "react";
import MD5 from "crypto-js/md5";
import { getDefaultLocaleConfig, isDevelopmentMode } from "../configuration/configuration";
import { logger, loggerError } from "./utils";
import { IDictionary } from "../types";
import { refreshToken } from "./security";
import { DateTime } from "luxon";
import EnvVars from "./variables";

const url = EnvVars.GRAPHQL_URI ?? "";
const excludeModules = [ "notification" ];

export interface IErrorData {
  message: string;
}

export interface IRequest {
  module: string;
  query: string;
  payload?: any;
}

export interface IOperationVariables<T> {
  loading: boolean;
  error: IErrorData | null;
  data: T | null;
}

interface ICacheData {
  expire: number;
  data: any;
}

interface IFetchDataContext<T> {
  loading: boolean;
  error: IErrorData | null;
  data: T | null;
}

let uniqueID: string | null = null;
const OneHour = 3600;
const OneMinute = 60;
const cache: IDictionary<ICacheData> = {};

const internalLogger = (module: string, message: string, ...args: any[]): void => {
  if (!excludeModules.includes(module)) {
    logger(message, ...args);
  }
};

const getCacheData = <T>(key: string): T | undefined | null => {
  const value = cache[key];
  if (value) {
    if (value.data) {
      value.expire = DateTime.now().toSeconds() + OneHour;
    }

    return value.data as T;
  }

  return undefined;
};

const addCacheData = (key: string, value: any): void => {
  const now = DateTime.now().toSeconds();

  cache[key] = {
    expire: now + (!value ? OneMinute : OneHour),
    data: value
  };

  const keys = Object.keys(cache);
  for (const k of keys) {
    if (cache[k] && cache[k].expire < now) {
      delete cache[key];
    }
  }
};

const getOptions = (token: string, request: IRequest | IRequest[], signal?: AbortSignal): any => {
  uniqueID = uniqueID ?? MD5(token).toString();

  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ request, uniqueID, locale: getDefaultLocaleConfig(), dev: isDevelopmentMode() }),
    signal
  };
};

export const resetUID = (): void => {
  uniqueID = null;
};

export const useFetch = <T>(request: IRequest | IRequest[], pollInterval = 0): IOperationVariables<T> => {
  const [update, setUpdate] = useState(-1);
  const [dataContext, setDataContext] = useState<IFetchDataContext<T>>({
    loading: false,
    error: null,
    data: null
  });

  console.log("useFetch", { request, pollInterval });

  const temporal = MD5(JSON.stringify(request)).toString();
  const moduleName = Array.isArray(request) ? request.map(r => r.module).join(", ") : request.module;
  const currentHash = useRef<string>(temporal);
  const intervalRef = useRef<any>(null);

  const executeFetch = useCallback((req: IRequest | IRequest[], loading = true) => {
    refreshToken().then(token => {
      const options = getOptions(token, req);
      const hash = MD5(options.body).toString();

      const cached = getCacheData<T>(hash);
      if (cached) {
        if (cached !== dataContext.data) {
          setDataContext({
            loading: false,
            error: null,
            data: cached
          });
        }

        return;
      }

      if (cached === null) {
        return;
      }

      addCacheData(hash, null);

      internalLogger(moduleName, "useFetch ->", req);

      if (loading) {
        setDataContext({
          loading,
          error: null,
          data: null
        });
      }

      fetch(url, options)
        .then(response => response.json())
        .then((responseData: T) => {
          internalLogger(moduleName, "useFetch <-", req, responseData);

          addCacheData(hash, responseData);
          setDataContext({
            loading: false,
            error: null,
            data: responseData
          });
        })
        .catch((err: Error) => {
          console.log(err);
          setDataContext({
            loading: false,
            error: { message: err.message },
            data: null
          });
        });
    });
  }, []);

  useEffect(() => {
    if (currentHash.current !== temporal) {
      currentHash.current = temporal;
      setUpdate((update + 1) % 10);
    }
  }, [request]);

  useEffect(() => {
    if (!intervalRef.current && pollInterval > 0) {
      intervalRef.current = setInterval(() => executeFetch(request, false), pollInterval);
      internalLogger(moduleName, "setInterval...", pollInterval, intervalRef.current);
      executeFetch(request);

      return () => {
        internalLogger(moduleName, "clearInterval...", intervalRef.current);
        clearInterval(intervalRef.current as any);
        intervalRef.current = null;
      };
    }

    if(pollInterval === 0) {
      executeFetch(request);
    }

  }, [pollInterval, update]);

  return { loading: dataContext.loading, error: dataContext.error, data: dataContext.data };
};

export const useLazyFetch = <T>(force = false): [ (request: IRequest | IRequest[]) => void, IOperationVariables<T>, () => void] => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<IErrorData | null>(null);
  const [data, setData] = useState<T | null>(null);

  const dispatch = useCallback((request: IRequest | IRequest[]): void => {
    if (!url) {
      return;
    }

    const moduleName = Array.isArray(request) ? request.map(r => r.module).join(", ") : request.module;

    refreshToken().then(token => {
      const options = getOptions(token, request);
      const hash = MD5(options.body).toString();

      if (!force) {
        const cached = getCacheData<T>(hash);
        if (cached) {
          if (cached !== data) {
            setData(cached);
          }

          return;
        }

        if (cached === null) {
          return;
        }
      }

      addCacheData(hash, null);

      internalLogger(moduleName, "useLazyFetch >>", request, hash);

      setLoading(true);
      fetch(url, options)
        .then(response => response.json())
        .then((responseData: T) => {
          internalLogger(moduleName, "useLazyFetch <<", request, responseData);

          addCacheData(hash, responseData);
          setData(responseData);
          setLoading(false);
        })
        .catch((err: Error) => {
          setError({ message: err.message });
          setLoading(false);
        });
    });
  }, []);

  const clearData = useCallback(()  => {
    setData(null);
  }, []);

  return [ dispatch, { loading, error, data }, clearData ];
};

export const useAsyncFetch = <T>(): [ (request: IRequest | IRequest[], disableLog?: true) => Promise<T | null>, IOperationVariables<T>] => {
  // const cache = useRef<IDictionary<any>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<IErrorData | null>(null);
  const [data, setData] = useState<T | null>(null);

  const dispatch = useCallback(async (request: IRequest | IRequest[], disableLog?: true): Promise<T | null> => {
    // const data = cache.current[url];
    // setData(data);
    // setLoading(false);

    const moduleName = Array.isArray(request) ? request.map(r => r.module).join(", ") : request.module;

    setLoading(true);
    try {
      const token = await refreshToken();
      const response = await fetch(url, getOptions(token, request));
      const responseData = await response.json();

      if (!disableLog) {
        internalLogger(moduleName, "useAsyncFetch <<", request, responseData);
      }

      setData(responseData);
      setLoading(false);

      return responseData as T;
    } catch (err) {
      loggerError("useAsyncFetch >>", request, (err as Error).message);
      setError({ message: (err as Error).message });
      setLoading(false);
    }

    return null;
  }, []);

  return [ dispatch, { loading, error, data }];
};

export const simpleFetch = async <T>(request: IRequest): Promise<T> => {

  return new Promise<T>((resolve, reject) => {
    refreshToken().then(token => {
      const options = getOptions(token, request);

      fetch(url, options)
        .then(response => response.json())
        .then((responseData: T) => {
          internalLogger(request.module, "useFetch <-", request, responseData);
          resolve(responseData);
        })
        .catch((err: Error) => {
          console.log(err);
          reject(err);
        });
    });
  });
};


window.addEventListener("beforeunload",  () => {
  // e.preventDefault();
  // e.returnValue = "";

  return undefined;
});
